import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { networkManager } from '../api/NetworkManager';
import { BreakthroughTips } from '../components/BreakthroughTip';
import { getTipsForContext } from '../lib/breakthrough';
import {
  Calendar,
  Users,
  Star,
  MessageSquare,
  Shield,
  Loader2,
  ArrowRight,
  Plus,
  AlertCircle,
  Zap,
  Crown,
  Rocket,
  Briefcase,
  FlaskConical,
  Trophy,
  Sparkles,
} from 'lucide-react';

interface EventItem {
  eventId: string;
  name: string;
  status: string;
  startDate: string;
  endDate: string;
  submissionDeadline?: string;
  isTest?: boolean;
  coverImageUrl?: string;
}

const SHOW_TEST_KEY = 'byte_show_test_events';

const ROLE_CONFIG: Record<string, { label: string; color: string; Icon: typeof Crown }> = {
  super_admin: { label: 'Super Admin', color: 'bg-purple-50 text-purple-700 border-purple-200', Icon: Crown },
  admin: { label: 'Admin', color: 'bg-red-50 text-red-700 border-red-200', Icon: Shield },
  organizer: { label: 'Organizer', color: 'bg-blue-50 text-blue-700 border-blue-200', Icon: Briefcase },
  judge: { label: 'Judge', color: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Star },
  mentor: { label: 'Mentor', color: 'bg-green-50 text-green-700 border-green-200', Icon: MessageSquare },
  participant: { label: 'Participant', color: 'bg-gray-50 text-gray-700 border-gray-200', Icon: Rocket },
};

const STATUS_META: Record<string, { label: string; color: string; bar: string; dot: string }> = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', bar: 'bg-gray-300', dot: 'bg-gray-400' },
  open: { label: 'Registration Open', color: 'bg-blue-100 text-blue-700', bar: 'bg-blue-400', dot: 'bg-blue-500' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-800', bar: 'bg-amber-400', dot: 'bg-amber-500' },
  submission_locked: { label: 'Submissions Locked', color: 'bg-orange-100 text-orange-700', bar: 'bg-orange-400', dot: 'bg-orange-500' },
  live_demo: { label: 'Live Demo', color: 'bg-purple-100 text-purple-700', bar: 'bg-purple-500', dot: 'bg-purple-500 animate-pulse' },
  judging: { label: 'Judging', color: 'bg-red-100 text-red-700', bar: 'bg-red-500', dot: 'bg-red-500 animate-pulse' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', bar: 'bg-green-400', dot: 'bg-green-500' },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-500', bar: 'bg-gray-300', dot: 'bg-gray-400' },
};

function getRelativeTime(dateStr: string | null | undefined): { label: string; urgent: boolean } {
  if (!dateStr) return { label: '', urgent: false };
  const target = new Date(dateStr);
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (Math.abs(diffDays) < 1) {
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    if (diffHours > 0) return { label: `in ${diffHours}h`, urgent: diffHours <= 6 };
    if (diffHours < 0) return { label: `${Math.abs(diffHours)}h ago`, urgent: false };
    return { label: 'today', urgent: true };
  }
  if (diffDays > 0 && diffDays <= 7) return { label: `in ${diffDays}d`, urgent: diffDays <= 2 };
  if (diffDays < 0 && diffDays >= -7) return { label: `${Math.abs(diffDays)}d ago`, urgent: false };
  return {
    label: target.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    urgent: false,
  };
}

function formatDateRange(start: string | null | undefined, end: string | null | undefined): string {
  if (!start || !end) return '';
  const s = new Date(start);
  const e = new Date(end);
  const sameYear = s.getFullYear() === e.getFullYear();
  const sameMonth = sameYear && s.getMonth() === e.getMonth();
  if (sameMonth) {
    return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}–${e.getDate()}, ${e.getFullYear()}`;
  }
  if (sameYear) {
    return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${e.getFullYear()}`;
  }
  return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

type EventRole = 'organizer' | 'judge' | 'mentor' | 'participant';

interface EnrichedEvent extends EventItem {
  myRoles: EventRole[];
  priority: number;
}

export function HomePage() {
  const { user, can } = useAuth();

  const [events, setEvents] = useState<EventItem[]>([]);
  const [userCount, setUserCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = can('admin');
  const canManageEvents = can('event:create') || can('event:edit');
  const isJudge = can('score:submit');
  const isMentor = can('feedback:give');

  const [showTest, setShowTest] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SHOW_TEST_KEY) === 'true';
    } catch {
      return false;
    }
  });

  function toggleShowTest() {
    const next = !showTest;
    setShowTest(next);
    try {
      localStorage.setItem(SHOW_TEST_KEY, next ? 'true' : 'false');
    } catch { /* ignore */ }
  }

  useEffect(() => {
    setLoading(true);
    const qs = isAdmin && showTest ? '?includeTest=true' : '';
    const promises: Promise<unknown>[] = [
      networkManager
        .get<{ data: EventItem[] }>(`/api/events${qs}`)
        .then((res) => setEvents(res.data ?? []))
        .catch(() => setEvents([])),
    ];
    if (isAdmin) {
      promises.push(
        networkManager
          .get<{ data: Array<{ sub: string }> }>('/api/users')
          .then((res) => setUserCount(res.data.length))
          .catch(() => setUserCount(0)),
      );
    }
    Promise.all(promises).finally(() => setLoading(false));
  }, [isAdmin, showTest]);

  // Enrich events with user's roles in each event + priority ordering
  const enrichedEvents = useMemo<EnrichedEvent[]>(() => {
    return events
      .filter((ev) => ev.status !== 'archived')
      .map((ev) => {
        const myRoles: EventRole[] = [];
        if (canManageEvents) myRoles.push('organizer');
        if (isJudge && ['submission_locked', 'live_demo', 'judging'].includes(ev.status)) myRoles.push('judge');
        if (isMentor && ['open', 'in_progress', 'submission_locked'].includes(ev.status)) myRoles.push('mentor');
        if (['open', 'in_progress'].includes(ev.status)) myRoles.push('participant');

        // Priority: judging/live = highest, in_progress = high, open = medium, rest = low
        let priority = 0;
        if (['live_demo', 'judging'].includes(ev.status)) priority = 4;
        else if (ev.status === 'submission_locked') priority = 3;
        else if (ev.status === 'in_progress') priority = 2;
        else if (ev.status === 'open') priority = 1;

        return { ...ev, myRoles, priority };
      })
      .sort((a, b) => b.priority - a.priority);
  }, [events, canManageEvents, isJudge, isMentor]);

  const actionRequired = enrichedEvents.filter(
    (ev) =>
      (ev.myRoles.includes('judge') && ['live_demo', 'judging', 'submission_locked'].includes(ev.status)) ||
      (['in_progress', 'open'].includes(ev.status) && ev.myRoles.includes('participant')),
  );
  const otherActive = enrichedEvents.filter((ev) => !actionRequired.includes(ev) && ev.status !== 'completed');
  const completed = enrichedEvents.filter((ev) => ev.status === 'completed');

  if (!user) return null;

  const displayName = user.name || user.email?.split('@')[0] || 'there';
  const firstName = displayName.split(' ')[0];

  return (
    <div className="animate-fade-in space-y-6">
      {/* Compact personal header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">
            Welcome back, {firstName}
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {user.roles.map((role) => {
              const config = ROLE_CONFIG[role] ?? ROLE_CONFIG.participant;
              const { Icon } = config;
              return (
                <span
                  key={role}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${config.color}`}
                >
                  <Icon className="h-3 w-3" />
                  {config.label}
                </span>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <button
              onClick={toggleShowTest}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                showTest
                  ? 'border-purple-300 bg-purple-50 text-purple-700'
                  : 'border-ring bg-white text-ink-secondary hover:bg-gray-50'
              }`}
              title="Toggle visibility of test events"
            >
              <FlaskConical className="h-3.5 w-3.5" />
              {showTest ? 'Hide test events' : 'Show test events'}
            </button>
          )}
          {canManageEvents && (
            <Link
              to="/events/new"
              className="btn-primary inline-flex items-center gap-1.5 text-sm w-fit"
            >
              <Plus className="h-4 w-4" /> New Event
            </Link>
          )}
        </div>
      </header>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-ink-tertiary" />
        </div>
      ) : (
        <>
          {/* Admin stats strip */}
          {isAdmin && (
            <div className="grid gap-3 sm:grid-cols-3">
              <StatCompact label="Events" value={events.length} Icon={Calendar} />
              <StatCompact label="Users" value={userCount ?? 0} Icon={Users} to="/admin/users" />
              <StatCompact
                label="Active Now"
                value={events.filter((e) => ['live_demo', 'judging', 'in_progress'].includes(e.status)).length}
                Icon={Zap}
                accent
              />
            </div>
          )}

          {/* Action Required */}
          {actionRequired.length > 0 && (
            <Section
              title="Needs your attention"
              icon={AlertCircle}
              accent
              description="Events waiting on something from you"
            >
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {actionRequired.map((ev) => (
                  <EventCard key={ev.eventId} event={ev} emphasize />
                ))}
              </div>
            </Section>
          )}

          {/* Your Active Events */}
          {otherActive.length > 0 && (
            <Section title="Active events" icon={Calendar} description="Events in progress or open for registration">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {otherActive.map((ev) => (
                  <EventCard key={ev.eventId} event={ev} />
                ))}
              </div>
            </Section>
          )}

          {/* Empty state */}
          {actionRequired.length === 0 && otherActive.length === 0 && (
            <div className="card flex flex-col items-center py-10 text-center">
              <Calendar className="h-10 w-10 text-ink-tertiary" />
              <p className="mt-3 font-medium text-ink">No active events</p>
              <p className="mt-1 text-sm text-ink-secondary">
                {canManageEvents ? 'Create a new event to get started' : 'Check back soon or browse past events'}
              </p>
              <Link to="/events" className="btn-secondary mt-4 text-sm">Browse all events</Link>
            </div>
          )}

          {/* Recaps — hero treatment so completed events feel like a destination,
              not an afterthought. First card is a hero; the rest follow at a
              slightly smaller size. */}
          {completed.length > 0 && (
            <section>
              <div className="mb-4 flex items-end justify-between gap-3">
                <div>
                  <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-ink sm:text-3xl">
                    <Trophy className="h-6 w-6 text-yellow-500" />
                    Hackathon recaps
                  </h2>
                  <p className="mt-1 text-sm text-ink-secondary">
                    See the winners, what they built, and what we&apos;re celebrating.
                  </p>
                </div>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                {completed.map((ev, i) => (
                  <EventRecapCard key={ev.eventId} event={ev} hero={i === 0 && completed.length === 1} />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* Breakthrough tips at the bottom */}
      <BreakthroughTips tips={getTipsForContext('dashboard')} title="Breakthrough mindset" />
    </div>
  );
}

/* ----------------- Components ----------------- */

function Section({
  title,
  icon: Icon,
  description,
  accent,
  subtle,
  children,
}: {
  title: string;
  icon: typeof Calendar;
  description?: string;
  accent?: boolean;
  subtle?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h2
            className={`flex items-center gap-2 text-base font-semibold ${
              accent ? 'text-amber-900' : subtle ? 'text-ink-secondary' : 'text-ink'
            }`}
          >
            <Icon className={`h-4 w-4 ${accent ? 'text-amber-600' : subtle ? 'text-ink-tertiary' : 'text-ink-secondary'}`} />
            {title}
          </h2>
          {description && <p className="mt-0.5 text-xs text-ink-tertiary">{description}</p>}
        </div>
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function StatCompact({
  label,
  value,
  Icon,
  to,
  accent,
}: {
  label: string;
  value: number;
  Icon: typeof Calendar;
  to?: string;
  accent?: boolean;
}) {
  const content = (
    <div
      className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${
        accent
          ? 'border-amber-200 bg-amber-50 hover:bg-amber-100'
          : 'border-ring bg-surface-raised hover:bg-surface-sunken'
      } ${to ? 'cursor-pointer' : ''}`}
    >
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-lg ${
          accent ? 'bg-amber-100 text-amber-700' : 'bg-accent-subtle text-ink'
        }`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-xl font-bold leading-tight ${accent ? 'text-amber-900' : 'text-ink'}`}>{value}</p>
        <p className="text-xs text-ink-secondary">{label}</p>
      </div>
      {to && <ArrowRight className="h-4 w-4 text-ink-tertiary" />}
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function EventCard({ event, emphasize }: { event: EnrichedEvent; emphasize?: boolean }) {
  const meta = STATUS_META[event.status] ?? STATUS_META.draft;
  const deadline = event.submissionDeadline ?? event.endDate;
  const relEnd = getRelativeTime(deadline);
  const relStart = getRelativeTime(event.startDate);
  const hasntStarted = relStart.label.startsWith('in ');
  const hasCover = !!event.coverImageUrl;

  return (
    <Link
      to={`/events/${event.eventId}`}
      className={`group relative overflow-hidden rounded-xl border bg-surface-raised transition-all hover:shadow-card-hover ${
        emphasize ? 'border-amber-300 shadow-sm' : 'border-ring'
      }`}
    >
      {/* Cover image or status color bar */}
      {hasCover ? (
        <div className="relative aspect-[16/7] overflow-hidden bg-gray-100">
          <img
            src={event.coverImageUrl}
            alt=""
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className={`absolute inset-x-0 top-0 h-1 ${meta.bar}`} />
        </div>
      ) : (
        <div className={`absolute inset-x-0 top-0 h-1 ${meta.bar}`} />
      )}

      <div className={hasCover ? 'p-4' : 'p-4 pt-5'}>
        {/* Top row: status + test badge + time */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-2xs font-semibold ${meta.color}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
              {meta.label}
            </span>
            {event.isTest && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-purple-100 px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider text-purple-700">
                <FlaskConical className="h-2.5 w-2.5" /> Test
              </span>
            )}
          </div>
          {relEnd.label && (
            <span className={`text-2xs font-medium ${relEnd.urgent ? 'text-red-600' : 'text-ink-tertiary'}`}>
              {hasntStarted ? `Starts ${relStart.label}` : `Ends ${relEnd.label}`}
            </span>
          )}
        </div>

        {/* Name */}
        <h3 className="mt-2.5 line-clamp-2 font-semibold text-ink group-hover:text-ink">{event.name}</h3>

        {/* Date range */}
        {event.startDate && event.endDate && (
          <p className="mt-1 text-xs text-ink-tertiary">{formatDateRange(event.startDate, event.endDate)}</p>
        )}

        {/* Role pills */}
        {event.myRoles.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {event.myRoles.map((role) => {
              const config = ROLE_CONFIG[role];
              if (!config) return null;
              const { Icon } = config;
              return (
                <span
                  key={role}
                  className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-2xs font-medium ${config.color}`}
                >
                  <Icon className="h-2.5 w-2.5" />
                  {config.label}
                </span>
              );
            })}
          </div>
        )}

        {/* Footer action hint */}
        <div className="mt-3 flex items-center justify-end text-xs text-ink-tertiary transition-colors group-hover:text-accent">
          Open <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </Link>
  );
}

function EventRecapCard({ event, hero }: { event: EnrichedEvent; hero?: boolean }) {
  const dates = formatDateRange(event.startDate, event.endDate);
  const cover = event.coverImageUrl;
  // The leaderboard tab gives the cleanest entry into a recap — winners,
  // top teams, and an explicit ceremony-mode link from there.
  const to = `/events/${event.eventId}?tab=leaderboard`;
  const heroClasses = hero ? 'lg:col-span-2 min-h-[260px]' : 'min-h-[220px]';
  const titleSize = hero ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-3xl';

  return (
    <Link
      to={to}
      className={`group relative block overflow-hidden rounded-2xl border border-ring shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg ${heroClasses}`}
    >
      {/* Background — cover image when available, otherwise a warm gold gradient
          so every card still looks like a celebration. */}
      {cover ? (
        <>
          <img
            src={cover}
            alt=""
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950/85 via-gray-900/55 to-gray-900/15" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500 via-yellow-600 to-orange-600" />
      )}

      {/* Content */}
      <div className="relative flex h-full flex-col justify-between p-5 sm:p-6">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-300 px-2.5 py-1 text-2xs font-bold uppercase tracking-[0.15em] text-yellow-950 shadow-sm">
            <Trophy className="h-3.5 w-3.5" /> Recap
          </span>
          {dates && (
            <span className="text-xs font-medium text-white/85 sm:text-sm">{dates}</span>
          )}
        </div>

        <div>
          <h3 className={`font-display font-extrabold leading-tight text-white ${titleSize}`}>
            {event.name}
          </h3>
          <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-white">
            <Sparkles className="h-4 w-4" />
            <span>See winners, products & feedback</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </div>
    </Link>
  );
}
